import React from 'react';
import { DateTime } from 'luxon';
import { sirenActions } from '~/utility/BinSentry-ui-utility';
import _ from 'lodash';
import { getSelectNewOrderEntityState, getOrdersAllocations, isoDateToDayKey, getActionFieldNames, getActionFields } from './orders-dialog-context-utilities';
import { syncOrderIntegration } from '../context/orders-dialog-sync-integration';
import { OrdersDialogSnackbarMessage } from '../components/orders-dialog-snackbar';
import { windowConfirmDeleteOrder } from '~/orders/dialogs/order-planner-dialog/context/orders-dialog-context-utilities';

export const createOrRequestOrder = async ({ state }) => {
  const { selectedBarnOrdersEntity, enqueueSnackbar } = state;
  const createAction = selectedBarnOrdersEntity.getAction('create-order');
  const requestAction = selectedBarnOrdersEntity.getAction('request-order');

  const action = createAction || requestAction;

  const { body: submitBody } = _finalOrderSubmissionBody({ state, action });
  const { entity: newOrderEntity } = await sirenActions.submitAction(action, submitBody);
  const newDaysAllocations = [...state.daysAllocations, ...getOrdersAllocations(newOrderEntity)];

  const updatedState = {
    ...state,
    daysOrders: [newOrderEntity, ...state.daysOrders],
    daysAllocations: newDaysAllocations,
  };

  const { success: syncSuccess, successMessage } = await syncOrderIntegration(newOrderEntity);

  enqueueSnackbar(
    <OrdersDialogSnackbarMessage
      integrationMessage={successMessage}
      orderEntity={newOrderEntity}
      actionVerb={newOrderEntity.properties.type === 'request' ? 'Requested' : 'Created'}
    />,
    { variant: 'success' },
  );
  // Intentionally not awaited
  _afterOrdersUpdate({ state, skipClose: !syncSuccess, orderEntity: newOrderEntity });

  return {
    ...updatedState,
    ...getSelectNewOrderEntityState({ state: updatedState, newOrderEntity }),
  };
};

export const updateOrder = async ({ state }) => {
  const { createOrEditSirenAction: action, selectedOrderId, enqueueSnackbar } = state;
  const { body: submitBody } = await _finalOrderSubmissionBody({ state, action });
  const { entity: newOrderEntity } = await sirenActions.submitAction(action, submitBody);

  const updatedDaysOrders = state.daysOrders.map(item => {
    if (item.getLinkByRel('self').href !== selectedOrderId) {
      return item;
    }

    return newOrderEntity;
  });

  const updatedAllocations = state.daysAllocations.filter(allocation => allocation.orderId !== selectedOrderId);
  updatedAllocations.push(...getOrdersAllocations(newOrderEntity));

  const updatedState = {
    ...state,
    daysOrders: updatedDaysOrders,
    daysAllocations: updatedAllocations,
  };

  const { success: syncSuccess, successMessage } = await syncOrderIntegration(newOrderEntity);

  enqueueSnackbar(
    <OrdersDialogSnackbarMessage
      integrationMessage={successMessage}
      orderEntity={newOrderEntity}
      actionVerb='Updated'
    />,
    { variant: 'success' },
  );

  // Intentionally not awaited
  _afterOrdersUpdate({ state, skipClose: !syncSuccess, orderEntity: newOrderEntity });

  return {
    ...updatedState,
    ...getSelectNewOrderEntityState({ state: updatedState, newOrderEntity }),
  };
};

export const updateAndConfirmOrder = async ({ state }) => {
  const { createOrEditSirenAction: action } = state;
  action.getField('state').value = 'scheduled';
  return updateOrder({ state });
};

export const approveOrder = async ({ state }) => {
  const { approveSirenAction, selectedOrderId, enqueueSnackbar } = state;
  const { entity: newOrderEntity } = await sirenActions.submitAction(approveSirenAction);

  const updatedDaysOrders = state.daysOrders.map(item => {
    if (item.getLinkByRel('self').href !== selectedOrderId) {
      return item;
    }

    return newOrderEntity;
  });

  const updatedAllocations = state.daysAllocations.filter(allocation => allocation.orderId !== selectedOrderId);
  updatedAllocations.push(...getOrdersAllocations(newOrderEntity));

  const updatedState = {
    ...state,
    daysOrders: updatedDaysOrders,
    daysAllocations: updatedAllocations,
  };

  const { success: syncSuccess, successMessage } = await syncOrderIntegration(newOrderEntity);

  enqueueSnackbar(
    <OrdersDialogSnackbarMessage
      integrationMessage={successMessage}
      orderEntity={newOrderEntity}
      actionVerb='Approved'
    />,
    { variant: 'success' },
  );

  // Intentionally not awaited
  _afterOrdersUpdate({ state, skipClose: !syncSuccess, orderEntity: newOrderEntity });

  return {
    ...updatedState,
    ...getSelectNewOrderEntityState({ state: updatedState }),
  };
};

export const cancelOrder = async ({ state }) => {
  if (!windowConfirmDeleteOrder()) {
    return state;
  }
  const { selectedOrderId, selectedOrderEntity, enqueueSnackbar } = state;
  await sirenActions.submitAction(selectedOrderEntity.getAction('close-order'));
  const updatedState = {
    ...state,
    daysOrders: state.daysOrders.filter(item => item.getLinkByRel('self').href !== selectedOrderId),
    daysAllocations:  state.daysAllocations.filter(item => item.orderId !== selectedOrderId),
  };

  enqueueSnackbar(
    <OrdersDialogSnackbarMessage
      actionVerb='Closed'
    />,
    { variant: 'success' },
  );

  // Intentionally not awaited
  _afterOrderClose({ state, orderEntity: selectedOrderEntity });

  return {
    ...updatedState,
    ...getSelectNewOrderEntityState({ state: updatedState }),
  };
};

const _finalOrderSubmissionBody = ({ state, action }) => {
  const { unsavedChanges, isoDate } = state;
  const noonDeliveryDateIso = DateTime.fromISO(
    unsavedChanges?.deliveryDate || isoDateToDayKey(isoDate),
  ).set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toISO();

  const existingFields = getActionFields(action);
  const requiredActionFieldNames = getActionFieldNames(action, ['required']);
  const allocationFieldsObject = _.pick(unsavedChanges, getActionFieldNames(action));

  const body = _.mapValues({
    ...existingFields,
    ...allocationFieldsObject,
    deliveryDate: noonDeliveryDateIso,
  }, function castNullAllocationToZero (value, key) {
    if (!key.startsWith('binAllocations')) {
      return value;
    }

    return value === null ? 0 : value;
  });

  const hasAllocation = Object.keys(body)
    .filter(key => key.startsWith('binAllocations'))
    .some(fieldName => !!body[fieldName]);

  const actionFieldsToDiff = _.omit(existingFields, ['quantity', 'orderNumber', 'state', 'type']);
  const hasUnsavedChanges = Object.keys(actionFieldsToDiff).some(key => {
    const bodyValue = body[key];
    const actionValue = actionFieldsToDiff[key];

    // If both the original value and the new payload data are falsy.
    // Assume no change.
    if (!bodyValue && !actionValue) {
      return false;
    }

    return bodyValue !== actionValue;
  });

  return {
    body,
    hasUnsavedChanges,
    hasEnoughData: hasAllocation && requiredActionFieldNames.every(key => _.get(body, key) !== undefined),
  };
};

export const hasAllOrderSubmissionBodyFieldValues = ({ state, action }) => _finalOrderSubmissionBody({ state, action })?.hasEnoughData;

export const hasAnyOrderSubmissionBodyFieldValues = ({ state, action }) => _finalOrderSubmissionBody({ state, action })?.hasUnsavedChanges;

const _afterOrdersUpdate = async ({ state, skipClose = false, orderEntity }) => {
  const { deliveryDate } = state?.selectedOrderEntity?.properties || {};
  await state?.onOrdersUpdate?.({
    isoDate: state.isoDate,
    initialIsoDate: deliveryDate || state.isoDate,
    orderId: state?.selectedOrderId,
    orderEntity,
  });
  if (!skipClose) {
    state?.onDialogClose();
  }
};

const _afterOrderClose = async ({ state, orderEntity }) => {
  await state?.onOrderClose?.({ isoDate: state?.isoDate, orderId: state?.selectedOrderId, orderEntity });
  state?.onDialogClose();
};
